import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  RefreshReactQuery,
  DOWNLOAD_FORMAT,
  DownloadFormatEnum,
  COLLABORATORS_PAGE_SIZE,
} from "../constants/AppConstants";
import CollaboratorsService from "../services/api/collaborators/Collaborators.service";
import CollaboratorsHistoryApprovalFlowService from "../services/api/collaborators/CollaboratorsHistoryApprovalFlow.service";
import CollaboratorsCurriculumsService from "../services/api/collaborators/CollaboratorsCurriculums.service";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import { useNotification } from "./notification.hook";
import { useEffect, useMemo, useCallback, useState } from "react";
import { useDebounce } from "use-debounce";

export const useCollaboratorCmSheets = (
  profileId: number | null,
  enabled: boolean = true,
) => {
  const { notification } = useNotification();
  const { data, isFetching, isError } = useQuery({
    queryKey: ["get-collaborator-cm-sheets", profileId],
    queryFn: () => CollaboratorsService.getCollaboratorCmSheets(profileId!),
    staleTime: RefreshReactQuery.HIGH,
    enabled: profileId !== null && enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching };
};

export const useCollaboratorDetails = (
  profileId: number,
  enabled: boolean = true,
) => {
  const { notification } = useNotification();
  const { data, isFetching, isLoading, isError } = useQuery({
    queryKey: ["get-collaborator-details", profileId],
    queryFn: () => CollaboratorsService.get(profileId),
    staleTime: RefreshReactQuery.HIGH,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching, isLoading };
};

export const useCollaboratorApprovalStatus = (
  profileId: number,
  enabled: boolean,
) => {
  const { notification } = useNotification();
  const { data, refetch, isError } = useQuery({
    queryKey: ["get-approval-status-all", profileId],
    queryFn: () =>
      CollaboratorsHistoryApprovalFlowService.getApprovalStatusAllSection(
        profileId,
      ),
    staleTime: RefreshReactQuery.HIGH,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, refetch: () => refetch().then(() => {}) };
};

export const useCollaboratorCurriculum = (
  collaboratorId: number | undefined,
  enabled: boolean,
) => {
  const { notification } = useNotification();
  const { data, isFetching, isError } = useQuery({
    queryKey: ["get-curriculums", collaboratorId],
    queryFn: () =>
      CollaboratorsCurriculumsService.get([
        { key: "collaboratorIds", value: [collaboratorId!] },
        {
          key: "fileExtensions",
          value: [DOWNLOAD_FORMAT[DownloadFormatEnum.PDF]],
        },
      ]),
    staleTime: RefreshReactQuery.HIGH,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching };
};

export const COLLABORATOR_ADVANCEMENT_QUERY_KEY =
  "get-collaborator-advancement";

export const useCollaboratorAdvancements = (profileId: number) => {
  const { notification } = useNotification();
  const { data, isFetching, refetch, isError } = useQuery({
    queryKey: [COLLABORATOR_ADVANCEMENT_QUERY_KEY, profileId],
    queryFn: () => CollaboratorsService.getAdvancement(profileId),
    staleTime: RefreshReactQuery.HIGH,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching, refetch: () => refetch().then(() => {}) };
};

export const useDownloadCollaboratorCv = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      collaboratorId,
      format,
    }: {
      collaboratorId: number;
      format: string;
    }) => {
      const params = [
        { key: "collaboratorIds", value: [collaboratorId] },
        { key: "fileExtensions", value: [format] },
      ];
      await CollaboratorsCurriculumsService.get(params);
      CollaboratorsCurriculumsService.download(params);
    },
  });

  return { downloadCv: mutateAsync, isDownloading: isPending };
};

export const useCareerManagersProfile = (profileId: number) => {
  const { notification } = useNotification();
  const { data, isFetching, isError } = useQuery({
    queryKey: [`get-collaborator-career-managers-${profileId}`],
    queryFn: () =>
      CollaboratorsService.getCollaboratorCarrerManagers(profileId),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching };
};

export const useDeleteCollaboratorCM = (profileId: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (cmId: number) => {
      return CollaboratorsService.deleteCollaboratorCM(profileId, cmId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-career-managers-${profileId}`],
      });
    },
  });

  return { mutateAsync, isPending };
};

export const useModifyCollaboratorCM = (profileId: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (cmId: number) => {
      return CollaboratorsService.modifyCollaboratorCM(profileId, cmId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-career-managers-${profileId}`],
      });
    },
  });

  return { mutateAsync, isPending };
};

export const useCollaboratorsToAssignCM = () => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: ["get-collaborators-to-assign-cm", debouncedSearch],
      queryFn: ({ pageParam, signal }) =>
        CollaboratorsService.list(
          COLLABORATORS_PAGE_SIZE,
          pageParam as number,
          [
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * COLLABORATORS_PAGE_SIZE < lastPage.pagination.total
          ? allPages.length
          : undefined,
      staleTime: RefreshReactQuery.MODERATE,
    });

  useEffect(() => {
    if (isError) notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const clearSearch = useCallback(() => setSearch(""), []);

  return {
    data: items,
    initLoading: isLoading,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    fetchingMore: isFetching && (data?.pages.length ?? 0) > 0,
    setSearch,
    search,
    clearSearch,
  };
};
